// Undo/Redo V1 (Feb 2026) -- snapshot, projection and restore helpers
// for the visible-grid undo stacks on the Roster and Rota Style pages.
// Frontend only: every restore goes through the SAME existing write
// endpoints (PUT /shifts, PUT /allocations,
// PUT /rota-style/profile/{id}/cells) so permissions, team scope,
// locked weeks, plan gates and validation all apply unchanged. No new
// backend routes, no new tables, no history persisted anywhere.
import { api } from "@/lib/api";

// ---------------- Roster (weekly grid) ----------------

/** Snapshot one roster cell (shift + user allocation entries) from a
 *  /api/roster response payload. Leave-sourced entries are excluded:
 *  the backend preserves and re-attaches those itself on every
 *  allocations write, so undo must never try to restore them. */
export function captureRosterCell(rosterData, employeeId, workDate) {
  const shift = (rosterData?.shifts || []).find(
    (s) => s.employee_id === employeeId && s.work_date === workDate) || null;
  const alloc = (rosterData?.allocations || []).find(
    (a) => a.employee_id === employeeId && a.work_date === workDate) || null;
  return {
    shift: shift ? {
      start_time: shift.start_time || null,
      end_time: shift.end_time || null,
      break_minutes: Number(shift.break_minutes || 0),
      break_start_time: shift.break_start_time || null,
      notes: shift.notes || null,
      location_id: shift.location_id || null,
    } : null,
    entries: (alloc?.entries || [])
      .filter((e) => !e.source_leave_id)
      .map((e) => ({
        category_id: e.category_id || null,
        minutes: Number(e.minutes || 0),
        start_time: e.start_time || null,
        end_time: e.end_time || null,
        note: e.note || null,
        combo_id: e.combo_id || null,
        combo_label: e.combo_label || null,
        location_id: e.location_id || null,
      })),
    location_id: alloc?.location_id ?? shift?.location_id ?? null,
  };
}

/** Canonical string projection of a roster cell snapshot. Used by the
 *  compare-and-warn concurrency guard. Order-insensitive on entries
 *  and ignores derived fields (total_minutes, category_name). */
export function projectRosterSnap(snap) {
  if (!snap) return "null";
  return JSON.stringify({
    s: snap.shift ? {
      st: snap.shift.start_time || null,
      et: snap.shift.end_time || null,
      bm: Number(snap.shift.break_minutes || 0),
      bs: snap.shift.break_start_time || null,
      no: snap.shift.notes || null,
      lo: snap.shift.location_id || null,
    } : null,
    lo: snap.location_id || null,
    e: (snap.entries || []).map((e) => JSON.stringify({
      c: e.category_id || null,
      m: Number(e.minutes || 0),
      st: e.start_time || null,
      et: e.end_time || null,
      n: e.note || null,
      cb: e.combo_id || null,
      lo: e.location_id || null,
    })).sort(),
  });
}

/** Write a roster cell snapshot back through the existing endpoints.
 *  A null shift restores the cleared state (the backend deletes the
 *  shift document when both times are null, exactly like Clear). */
export async function applyRosterSnapshot(employeeId, workDate, snap) {
  if (snap.shift) {
    await api.put("/shifts", {
      employee_id: employeeId,
      work_date: workDate,
      start_time: snap.shift.start_time,
      end_time: snap.shift.end_time,
      break_minutes: snap.shift.break_minutes,
      break_start_time: snap.shift.break_start_time,
      notes: snap.shift.notes,
      location_id: snap.shift.location_id,
    });
  } else {
    await api.put("/shifts", {
      employee_id: employeeId,
      work_date: workDate,
      start_time: null,
      end_time: null,
      break_minutes: 0,
      break_start_time: null,
      notes: null,
      location_id: null,
    });
  }
  await api.put("/allocations", {
    employee_id: employeeId,
    work_date: workDate,
    entries: (snap.entries || []).map((e) => ({
      category_id: e.category_id,
      minutes: e.minutes,
      start_time: e.start_time,
      end_time: e.end_time,
      note: e.note,
      combo_id: e.combo_id,
      combo_label: e.combo_label,
      location_id: e.location_id,
    })),
    location_id: snap.location_id,
  });
}

// ---------------- Rota Style (pattern grid) ----------------

/** The single current cell at (employee, cycle day). Rota Style V1
 *  allows one block per employee per day; if legacy data ever holds
 *  more than one, the earliest by start_time is treated as current
 *  and the compare-and-warn guard surfaces the mismatch. */
export function rsCellAtSlot(cellList, employeeId, dayIndex) {
  const matches = (cellList || []).filter(
    (c) => c.employee_id === employeeId && c.cycle_day_index === dayIndex);
  matches.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  return matches[0] || null;
}

/** Snapshot a rota style cell. Null means "no cell at this slot". */
export function snapRotaCell(cell) {
  if (!cell) return null;
  return {
    start_time: cell.start_time || null,
    end_time: cell.end_time || null,
    break_minutes: Number(cell.break_minutes || 0),
    break_start_time: cell.break_start_time || null,
    note: cell.note ?? null,
    assignments: (cell.assignments || []).map((a) => ({
      category_id: a.category_id,
      minutes: (typeof a.minutes === "number" && a.minutes > 0)
        ? Math.round(a.minutes)
        : null,
      start_time: a.start_time || null,
      end_time: a.end_time || null,
    })),
  };
}

/** Canonical string projection of a rota style cell snapshot. */
export function projectRotaSnap(snap) {
  if (!snap) return "null";
  return JSON.stringify({
    st: snap.start_time || null,
    et: snap.end_time || null,
    bm: Number(snap.break_minutes || 0),
    bs: snap.break_start_time || null,
    n: snap.note ?? null,
    a: (snap.assignments || []).map((a) => JSON.stringify({
      c: a.category_id,
      m: a.minutes || null,
      st: a.start_time || null,
      et: a.end_time || null,
    })).sort(),
  });
}

/** Build the PUT /rota-style/profile/{id}/cells body that restores a
 *  slot to `target` (null = remove the cell). Mirrors saveCell's own
 *  logic: the backend upsert key includes start/end, so the current
 *  row is deleted only when the restored times differ. The backend
 *  processes upserts before deletes, so same-time restores must NOT
 *  delete (the upsert updates the row in place). */
export function buildRotaRestoreBody(employeeId, dayIndex, target, currentCell) {
  const body = { upsert: [], delete_cell_ids: [] };
  if (target) {
    body.upsert.push({
      employee_id: employeeId,
      cycle_day_index: dayIndex,
      start_time: target.start_time,
      end_time: target.end_time,
      assignments: (target.assignments || []).map((a) => {
        const out = { category_id: a.category_id };
        if (a.minutes) out.minutes = a.minutes;
        if (a.start_time && a.end_time) {
          out.start_time = a.start_time;
          out.end_time = a.end_time;
        }
        return out;
      }),
      break_minutes: target.break_minutes || 0,
      break_start_time: target.break_start_time,
      note: target.note,
    });
    if (currentCell
        && ((currentCell.start_time || null) !== target.start_time
            || (currentCell.end_time || null) !== target.end_time)) {
      body.delete_cell_ids.push(currentCell.cell_id);
    }
  } else if (currentCell) {
    body.delete_cell_ids.push(currentCell.cell_id);
  }
  return body;
}
